import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'

import type { AgriMaterialStockInNote } from '@/lib/api/agri-material-lot-api'
import type { AgriInSubmit } from '@/components/agricultural-materials/agri-in-note-form'
import type {
    AgriInNoteFormReason,
    AgriInNoteFormValues,
} from '@/lib/schemas/agri-note-schemas'
import { AgriInNoteForm } from '@/components/agricultural-materials/agri-in-note-form'
import { useAuth } from '@/hooks/use-auth'
import {
    useAgriInNote,
    useUpdateAgriInNote,
} from '@/hooks/use-agri-material-lot-query'
import { m } from '@/lib/paraglide/messages'
import { isAgriInNoteFormReason } from '@/lib/schemas/agri-note-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/stock-in-notes/$id_/edit',
)({
    component: EditAgriInNotePage,
})

export function EditAgriInNotePage() {
    const { id } = Route.useParams()
    const { user, hasPermission } = useAuth()
    const { data: res, isLoading } = useAgriInNote(id)
    const note = res?.data

    if (!hasPermission('agri_material_lots.write')) {
        return (
            <div className="p-6 text-sm text-muted-foreground">
                {m.aml_no_permission_create()}
            </div>
        )
    }
    if (isLoading) {
        return (
            <div className="p-6 text-muted-foreground">{m.aml_loading()}</div>
        )
    }
    if (!note) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.aml_note_not_found()}
            </div>
        )
    }
    const editableReason = isAgriInNoteFormReason(note.reason)
        ? note.reason
        : null
    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && editableReason !== null

    if (!editable || editableReason === null) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.pl_pnk_edit_not_allowed()}
            </div>
        )
    }
    return <EditForm note={note} reason={editableReason} />
}

function EditForm({
    note,
    reason,
}: {
    note: AgriMaterialStockInNote
    reason: AgriInNoteFormReason
}) {
    const navigate = useNavigate()
    const update = useUpdateAgriInNote()

    const back = () =>
        navigate({
            to: '/agricultural-materials/stock-in-notes/$id',
            params: { id: note.id },
        })

    const initialValues: AgriInNoteFormValues = {
        reason,
        notes: note.notes ?? '',
        // Item của phiếu đang sửa đều trỏ tới lô đã tạo → seed dạng 'existing'
        // (user vẫn có thể đổi sang 'new' hoặc thêm dòng).
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            agri_material_lot_id: it.agri_material_lot_id,
            lot_code: it.lot_code,
            material_name: it.material_name,
            material_unit: it.material_unit,
            quantity: it.quantity,
        })),
    }

    const handleSubmit = async (
        value: AgriInSubmit,
        workflow: Parameters<
            React.ComponentProps<typeof AgriInNoteForm>['onSubmit']
        >[1],
    ) => {
        try {
            await workflow.run(
                () => update.mutateAsync({ id: note.id, req: value }),
                (res) => res.data?.id,
            )
            await back()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <AgriInNoteForm
            mode="edit"
            initialValues={initialValues}
            isPending={update.isPending}
            onSubmit={handleSubmit}
            onCancel={back}
        />
    )
}
